// LAYOUT-SPEC §0 — a team's Work products tab (SAD-859)
//
//   ONE QUESTION  What did this team produce that nobody is paying for, and
//                 can we prove it?
//   PRIMARY OBJECT  The team's work products (Archetype B, a collection).
//   PRIMARY VERB    + Log work product. Promote is the PM's follow-up.
//   WORST-CASE VOLUME  A few hundred over a contract year.
//
// The drawer asks for three things (title, date, who asked); everything else
// is optional. Logging never blocks on the duplicate hint: it only offers the
// SOW deliverable the title reads like.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraAttachmentRow, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraDateField, SpecraEmptyState, SpecraFilterToolbar, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraSelect, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileText, Info, Link2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useArtifactNames, useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { useCmDrawerParams } from '../drawerParams';
import { useCreateWorkProduct, usePromoteWorkProduct, useUpdateWorkProduct, useWorkProductDuplicates, useWorkProducts } from '../hooks';
import { cmDate } from '../lib/format';
import { commercialLabel, defaultCommercial, defaultScope, filterWorkProducts, formatCents, formatHours, monthLabel, monthOf, parseDollars, parseHours, SCOPE_BUCKETS, scopeBucket, scopeLabel, scopeTone, WHO_ASKED, whoAskedLabel } from '../lib/workProducts';
import { CmDrawer } from './CmDrawer';
import { CommentThread } from './CommentThread';
import { CmTable } from './common';
import { useTeamWriter } from './TeamMembers';
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// specra-allow local-composite: SpecraBadge with the scope vocabulary's label and tone, no restyle
export function ScopeBadge({ wp }) {
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: scopeTone(wp.scopeAssessment),
        "data-testid": Testids.cmWorkProducts.scopeBadge,
        "data-state": scopeBucket(wp.scopeAssessment),
        children: scopeLabel(wp.scopeAssessment)
    });
}
function pwsLine(wp) {
    if (wp.notInScopeDocs) return 'Not in PWS / Schedule B';
    return wp.pwsRef ? `PWS ${wp.pwsRef}` : 'No section named';
}
// ---------------------------------------------------------------------------
// Log / edit drawer
// ---------------------------------------------------------------------------
export function LogWorkProductDrawer({ programId, workstreamId, teamName, existing, onClose }) {
    const create = useCreateWorkProduct(programId, {
        success: 'Work product logged',
        errors: 'inline'
    });
    const update = useUpdateWorkProduct(programId, {
        success: 'Work product saved',
        errors: 'inline'
    });
    const upload = useUploadArtifacts();
    const [title, setTitle] = useState(existing?.title ?? '');
    const [producedOn, setProducedOn] = useState(existing?.producedOn ?? todayISO());
    const [whoAsked, setWhoAsked] = useState(existing ? String(existing.authorityBasis) : '');
    const [pwsRef, setPwsRef] = useState(existing?.pwsRef ?? '');
    const [notInPws, setNotInPws] = useState(existing?.notInScopeDocs ?? false);
    const [hours, setHours] = useState(existing?.hours === undefined ? '' : String(existing.hours));
    const [value, setValue] = useState(existing?.valueCents === undefined ? '' : (Number(existing.valueCents) / 100).toFixed(2));
    const [files, setFiles] = useState([]);
    const [kept, setKept] = useState(existing?.attachments ?? []);
    const keptNames = useArtifactNames(useMemo(()=>kept.map((a)=>a.artifactId).filter(Boolean), [
        kept
    ]));
    const dupes = useWorkProductDuplicates(programId, workstreamId, existing ? '' : title);
    const hoursN = parseHours(hours);
    const centsN = parseDollars(value);
    const badNumber = Number.isNaN(hoursN) || Number.isNaN(centsN);
    const busy = create.isPending || update.isPending || upload.isPending;
    const ready = title.trim() !== '' && producedOn !== '' && whoAsked !== '' && !badNumber && !busy;
    const scope = defaultScope(notInPws ? '' : pwsRef);
    const err = create.error ?? update.error ?? upload.error;
    const save = async ()=>{
        const uploaded = files.length ? await upload.mutateAsync({
            files
        }) : {
            files: [],
            failed: []
        };
        if (uploaded.failed.length) return;
        const fields = {
            title: title.trim(),
            producedOn,
            authorityBasis: Number(whoAsked),
            pwsRef: notInPws ? '' : pwsRef.trim(),
            notInScopeDocs: notInPws,
            hours: hoursN,
            valueCents: centsN === undefined ? undefined : BigInt(centsN),
            // SAD-863: the notes became the work product's comment thread, which
            // shows them as an imported comment; saving keeps them as they were.
            description: existing?.description ?? ''
        };
        const attachments = [
            ...kept.map((a)=>({
                    artifactId: a.artifactId,
                    url: a.url,
                    label: a.label
                })),
            ...uploaded.files.map((f)=>({
                    artifactId: f.artifactId,
                    label: f.name
                }))
        ];
        if (existing) {
            const changedFiles = files.length > 0 || kept.length !== existing.attachments.length;
            update.mutate({
                workProductId: existing.id,
                fields,
                replaceAttachments: changedFiles,
                attachments: changedFiles ? attachments : []
            }, {
                onSuccess: onClose
            });
        } else {
            create.mutate({
                workstreamId,
                fields,
                attachments
            }, {
                onSuccess: onClose
            });
        }
    };
    const match = dupes.data?.[0];
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: existing ? 'Edit work product' : 'Log work product',
        description: `Something ${teamName} produced. It stays on record, and out-of-scope work goes into a mod or REA package.`,
        onClose: onClose,
        busy: busy,
        testId: Testids.cmWorkProducts.drawer,
        footerProps: {
            confirmLabel: existing ? 'Save' : 'Log work product',
            onConfirm: ()=>void save(),
            confirmDisabled: !ready,
            loading: busy,
            loadingLabel: upload.isPending ? 'Uploading…' : 'Saving…',
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmWorkProducts.save
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "What did the team produce?",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        autoFocus: true,
                        placeholder: "e.g. Weekend firewall migration runbook",
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmWorkProducts.title
                    })
                }),
                match ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "info",
                    variant: "framed",
                    "data-testid": Testids.cmWorkProducts.duplicateHint,
                    title: `This looks like the SOW deliverable “${match.title}”`,
                    children: [
                        "If it is, attach the files there instead, so it counts as delivered.",
                        ' ',
                        /*#__PURE__*/ _jsxs(SpecraLink, {
                            href: `/delivery/programs/${programId}/deliverables/${match.dutyId}`,
                            renderLink: renderRouterLink,
                            "data-testid": Testids.cmWorkProducts.duplicateLink,
                            children: [
                                "Open ",
                                match.title
                            ]
                        }),
                        ". If it is something else, carry on."
                    ]
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "Date produced",
                            required: true,
                            value: producedOn,
                            onValueChange: setProducedOn,
                            "data-testid": Testids.cmWorkProducts.producedOn
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Who asked?",
                            required: true,
                            hint: WHO_ASKED.find((w)=>String(w.value) === whoAsked)?.hint,
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                value: whoAsked,
                                onChange: (e)=>setWhoAsked(e.target.value),
                                "data-testid": Testids.cmWorkProducts.whoAsked,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Pick one…"
                                    }),
                                    WHO_ASKED.map((w)=>/*#__PURE__*/ _jsx("option", {
                                            value: String(w.value),
                                            children: w.label
                                        }, w.value))
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "PWS / Schedule B section",
                    hint: "Name the section if one covers it. Leave both empty if you are not sure.",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: notInPws ? '' : pwsRef,
                                disabled: notInPws,
                                placeholder: "e.g. 3.2.1",
                                onChange: (e)=>setPwsRef(e.target.value),
                                "data-testid": Testids.cmWorkProducts.pwsRef
                            }),
                            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: "Not in the PWS or Schedule B",
                                "data-testid": Testids.cmWorkProducts.notInPws,
                                controlProps: {
                                    checked: notInPws,
                                    onCheckedChange: (c)=>{
                                        setNotInPws(c === true);
                                        if (c === true) setPwsRef('');
                                    }
                                }
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Hours",
                            hint: "Optional.",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                inputMode: "decimal",
                                value: hours,
                                placeholder: "e.g. 12",
                                onChange: (e)=>setHours(e.target.value),
                                "data-testid": Testids.cmWorkProducts.hours
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Value (USD)",
                            hint: "Optional. What it would cost the government.",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                inputMode: "decimal",
                                value: value,
                                placeholder: "e.g. 4,800",
                                onChange: (e)=>setValue(e.target.value),
                                "data-testid": Testids.cmWorkProducts.value
                            })
                        })
                    ]
                }),
                badNumber ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: "Hours and value are plain numbers, like 12 or 4,800."
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Evidence",
                    hint: "Optional. The email, the file, a screenshot: whatever proves it.",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        "data-testid": Testids.cmWorkProducts.files,
                        children: [
                            /*#__PURE__*/ _jsx(FileUploader, {
                                files: files,
                                onChange: setFiles,
                                disabled: busy,
                                inputTestid: Testids.cmWorkProducts.filesInput
                            }),
                            kept.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-1.5",
                                children: kept.map((a)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraAttachmentRow, {
                                            name: a.url || keptNames.get(a.artifactId) || a.label || 'File',
                                            icon: a.url ? /*#__PURE__*/ _jsx(Link2, {
                                                className: "h-3.5 w-3.5",
                                                "aria-hidden": true
                                            }) : /*#__PURE__*/ _jsx(FileText, {
                                                className: "h-3.5 w-3.5",
                                                "aria-hidden": true
                                            }),
                                            onRemove: ()=>setKept(kept.filter((k)=>k.id !== a.id))
                                        })
                                    }, a.id))
                            }) : null
                        ]
                    })
                }),
                existing ? null : /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    className: "flex items-center gap-1.5",
                    "data-testid": Testids.cmWorkProducts.defaults,
                    "data-scope": scopeBucket(scope),
                    children: [
                        "Will be logged as ",
                        scopeLabel(scope),
                        " · ",
                        commercialLabel(defaultCommercial(scope)),
                        /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "Unsure until a PWS section is named. The PM settles it in the monthly Beyond Scope pass; out-of-scope work becomes a potential change.",
                            children: /*#__PURE__*/ _jsx(Info, {
                                className: "h-3.5 w-3.5",
                                "aria-label": "How this is decided"
                            })
                        })
                    ]
                }),
                err ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(err, 'Could not save')
                }) : null,
                existing ? /*#__PURE__*/ _jsx(CommentThread, {
                    programId: programId,
                    target: {
                        type: 'work_product',
                        id: existing.id
                    }
                }) : null
            ]
        })
    });
}
/** A reader's view of a work product: what it is, and its comment thread. */ function WorkProductCommentsDrawer({ programId, wp, onClose }) {
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: wp.title,
        description: `${pwsLine(wp)} · produced ${cmDate(wp.producedOn)}`,
        onClose: onClose,
        testId: Testids.cmWorkProducts.drawer,
        children: /*#__PURE__*/ _jsx(CommentThread, {
            programId: programId,
            target: {
                type: 'work_product',
                id: wp.id
            }
        })
    });
}
// ---------------------------------------------------------------------------
// Promote
// ---------------------------------------------------------------------------
export function PromoteDrawer({ programId, wp, onClose }) {
    const navigate = useNavigate();
    const params = useCmDrawerParams();
    const promote = usePromoteWorkProduct(programId, {
        success: 'Now a scheduled deliverable',
        errors: 'inline'
    });
    const [title, setTitle] = useState(wp.title);
    const [scheduleNext, setScheduleNext] = useState(true);
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Make it a scheduled deliverable",
        description: "The team will owe it from now on. The work product stays as its history.",
        onClose: onClose,
        busy: promote.isPending,
        testId: Testids.cmWorkProducts.promoteDrawer,
        footerProps: {
            confirmLabel: 'Promote',
            confirmDisabled: !title.trim() || promote.isPending,
            loading: promote.isPending,
            onCancel: onClose,
            onConfirm: ()=>promote.mutate({
                    workProductId: wp.id,
                    title: title.trim()
                }, {
                    onSuccess: (r)=>{
                        onClose();
                        const dutyId = r.duty?.id ?? '';
                        if (!scheduleNext || !dutyId) return;
                        if (r.sowId) params.open('assign', `${wp.workstreamId}:${r.sowId}:${dutyId}`);
                        else navigate(`/delivery/programs/${programId}/deliverables/${dutyId}`);
                    }
                }),
            confirmProps: {
                'data-testid': Testids.cmWorkProducts.promoteSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Deliverable title",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmWorkProducts.promoteTitle
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: "It is performed under the team’s latest SOW. With no SOW on file it starts as a deliverable with no schedule."
                }),
                /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                    label: "Set its schedule next",
                    controlProps: {
                        checked: scheduleNext,
                        onCheckedChange: (c)=>setScheduleNext(c === true)
                    }
                }),
                promote.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(promote.error, 'Could not promote')
                }) : null
            ]
        })
    });
}
// ---------------------------------------------------------------------------
// Columns shared by the team tab and Beyond Scope
// ---------------------------------------------------------------------------
export function useWorkProductColumns(programId, extra = {}) {
    return [
        ...extra.lead ?? [],
        {
            id: 'title',
            header: 'Work product',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "flex flex-col",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: r.title
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                            children: [
                                pwsLine(r),
                                r.attachments.length ? ` · ${r.attachments.length} file${r.attachments.length === 1 ? '' : 's'}` : ' · no evidence yet'
                            ]
                        })
                    ]
                })
        },
        {
            id: 'asked',
            header: 'Who asked',
            cell: (r)=>whoAskedLabel(r.authorityBasis)
        },
        {
            id: 'scope',
            header: 'Scope',
            cell: (r)=>r.promotedToDutyId ? /*#__PURE__*/ _jsx(SpecraLink, {
                    href: `/delivery/programs/${programId}/deliverables/${r.promotedToDutyId}`,
                    renderLink: renderRouterLink,
                    onClick: (e)=>e.stopPropagation(),
                    "data-testid": Testids.cmWorkProducts.promoted,
                    children: "Scheduled"
                }) : /*#__PURE__*/ _jsx(ScopeBadge, {
                    wp: r
                })
        },
        {
            id: 'produced',
            header: 'Produced',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(r.producedOn)
                })
        },
        {
            id: 'effort',
            header: 'Hours · value',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "tabular-nums text-muted-foreground",
                    children: [
                        r.hours === undefined ? '—' : formatHours(r.hours),
                        ' · ',
                        r.valueCents === undefined ? '—' : formatCents(Number(r.valueCents))
                    ]
                })
        }
    ];
}
// ---------------------------------------------------------------------------
// The team tab
// ---------------------------------------------------------------------------
export function WorkProductsPanel({ programId, wsId, teamName }) {
    const writer = useTeamWriter(programId);
    const canLog = writer.canLogFor(wsId);
    const q = useWorkProducts(programId, {
        workstreamId: wsId
    });
    const [scope, setScope] = useState('');
    const [month, setMonth] = useState('');
    const [drawer, setDrawer] = useState(null);
    const [promoting, setPromoting] = useState(null);
    const [reading, setReading] = useState(null);
    const all = q.data ?? [];
    const months = useMemo(()=>[
            ...new Set(all.map((r)=>monthOf(r.producedOn)))
        ].sort().reverse(), [
        all
    ]);
    const rows = filterWorkProducts(all, {
        scope,
        month
    });
    const columns = useWorkProductColumns(programId);
    const logButton = canLog ? /*#__PURE__*/ _jsx(SpecraButton, {
        onClick: ()=>setDrawer({}),
        "data-testid": Testids.cmWorkProducts.logButton,
        children: "+ Log work product"
    }) : null;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": Testids.cmWorkProducts.panel,
        children: [
            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                search: false,
                filters: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Filter by scope",
                            size: "sm",
                            className: "w-auto",
                            value: scope,
                            onChange: (e)=>setScope(e.target.value),
                            "data-testid": Testids.cmWorkProducts.filterScope,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Any scope"
                                }),
                                SCOPE_BUCKETS.map((b)=>/*#__PURE__*/ _jsx("option", {
                                        value: b.value,
                                        children: b.label
                                    }, b.value))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Filter by month",
                            size: "sm",
                            className: "w-auto",
                            value: month,
                            onChange: (e)=>setMonth(e.target.value),
                            "data-testid": Testids.cmWorkProducts.filterMonth,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Any month"
                                }),
                                months.map((m)=>/*#__PURE__*/ _jsx("option", {
                                        value: m,
                                        children: monthLabel(m)
                                    }, m))
                            ]
                        })
                    ]
                }),
                resultSummary: `${rows.length} of ${all.length}`,
                actions: logButton
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: `Work products of ${teamName}`,
                rows: rows,
                columns: columns,
                rowKey: (r)=>r.id,
                testId: Testids.cmWorkProducts.table,
                rowTestId: Testids.cmWorkProducts.row,
                isLoading: q.isLoading,
                error: q.error,
                onRetry: ()=>void q.refetch(),
                onRowClick: (r)=>canLog ? setDrawer({
                        wp: r
                    }) : setReading(r),
                getRowLabel: (r)=>r.title,
                rowActions: writer.runsDelivery ? (r)=>r.promotedToDutyId ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: (e)=>{
                            e.stopPropagation();
                            setPromoting(r);
                        },
                        "data-testid": Testids.cmWorkProducts.promote,
                        children: "Promote"
                    }) : undefined,
                empty: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.cmWorkProducts.empty,
                    children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        framed: true,
                        title: all.length ? 'Nothing matches these filters' : 'Nothing logged yet',
                        description: all.length ? 'Clear a filter to see the rest.' : 'Log what this team produces that no deliverable covers: an extra report, a weekend fix, a briefing the COR asked for. It is the record that gets that work paid for.',
                        action: all.length ? undefined : logButton
                    })
                })
            }),
            drawer ? /*#__PURE__*/ _jsx(LogWorkProductDrawer, {
                programId: programId,
                workstreamId: wsId,
                teamName: teamName,
                existing: drawer.wp,
                onClose: ()=>setDrawer(null)
            }) : null,
            promoting ? /*#__PURE__*/ _jsx(PromoteDrawer, {
                programId: programId,
                wp: promoting,
                onClose: ()=>setPromoting(null)
            }) : null,
            reading ? /*#__PURE__*/ _jsx(WorkProductCommentsDrawer, {
                programId: programId,
                wp: reading,
                onClose: ()=>setReading(null)
            }) : null
        ]
    });
}
